export const MAX_IMAGE_DIMENSION = 1920;
export const JPEG_QUALITY = 0.88;
export const IMAGE_PROCESSING_VERSION = 'mobile-normalize-v3';
export const IMAGE_PROCESSING_ERROR = 'Não foi possível processar esta foto. Tente outra imagem.';
const OUTPUT_TYPE='image/jpeg';
const MAX_BYTES=5*1024*1024;
const extensionType=(name='')=>({jpg:'image/jpeg',jpeg:'image/jpeg',png:'image/png',webp:'image/webp',heic:'image/heic',heif:'image/heif'})[name.toLowerCase().split('.').pop()] || '';
const readBuffer=async blob=>{
  if(blob.arrayBuffer){try{return await blob.arrayBuffer();}catch{/* Fall back to FileReader for mobile file providers. */}}
  return new Promise((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=()=>reject(reader.error || new Error('File read failed'));reader.onabort=()=>reject(new Error('File read cancelled'));reader.readAsArrayBuffer(blob);});
};
export const detectImageType=async file=>{
 const bytes=new Uint8Array(await readBuffer(file.slice(0,64)));
 if(bytes[0]===255 && bytes[1]===216 && bytes[2]===255)return 'image/jpeg';
 if(bytes.length>=8 && bytes.slice(0,8).every((v,i)=>v===[137,80,78,71,13,10,26,10][i]))return 'image/png';
 if(String.fromCharCode(...bytes.slice(0,4))==='RIFF' && String.fromCharCode(...bytes.slice(8,12))==='WEBP')return 'image/webp';
 const brands=String.fromCharCode(...bytes.slice(4)).toLowerCase();
 if(brands.includes('ftyp') && /(heic|heix|hevc|hevx|mif1|msf1)/.test(brands))return /(heic|heix|hevc|hevx)/.test(brands)?'image/heic':'image/heif';
 return file.type?.toLowerCase() || extensionType(file.name);
};
const nativeImage=async blob=>{
 const url=URL.createObjectURL(blob);const image=new Image();let timer;
 const dispose=()=>{clearTimeout(timer);image.onload=null;image.onerror=null;image.removeAttribute?.('src');URL.revokeObjectURL(url);};
 try{
  await new Promise((resolve,reject)=>{
   image.onload=resolve;image.onerror=()=>reject(new Error('Native decoder failed'));
   timer=setTimeout(()=>reject(new Error('Native decoder timed out')),10000);image.src=url;
  });
  clearTimeout(timer);
  // Keep both the source and object URL alive until canvas serialization completes.
  return {image,dispose,method:'native'};
 }catch(error){dispose();throw error;}
};
const deadline=(promise,ms,onLate)=>new Promise((resolve,reject)=>{
 let expired=false;const timer=setTimeout(()=>{expired=true;reject(new Error('Decoder timed out'));},ms);
 promise.then(value=>{clearTimeout(timer);if(expired)onLate?.(value);else resolve(value);},error=>{clearTimeout(timer);if(!expired)reject(error);});
});
const loadImage=async blob=>{
 try{return await nativeImage(blob);}catch{/* Native Safari decoding is preferred, including HEIC support. */}
 if(typeof createImageBitmap==='function'){
  let image;
  try{image=await deadline(createImageBitmap(blob,{imageOrientation:'from-image'}),10000,image=>image.close());}
  catch(error){
   if(!['TypeError','NotSupportedError'].includes(error.name))throw error;
   image=await deadline(createImageBitmap(blob),10000,image=>image.close());
  }
  return {image,dispose:()=>image.close(),method:'bitmap'};
 }
 throw new Error('No supported decoder');
};
const canvasBlob=(canvas,quality)=>new Promise((resolve,reject)=>{
 let done=false;
 const finish=blob=>{if(done)return;done=true;clearTimeout(timer);blob?.size?resolve(blob):reject(new Error('Canvas export failed'));};
 const timer=setTimeout(()=>finish(null),10000);
 const fallback=()=>{
  try{
   const data=canvas.toDataURL(OUTPUT_TYPE,quality);const encoded=data.split(',')[1];
   if(!encoded)throw new Error('Empty canvas');
   const binary=atob(encoded);const bytes=new Uint8Array(binary.length);for(let i=0;i<binary.length;i++)bytes[i]=binary.charCodeAt(i);
   finish(new Blob([bytes],{type:OUTPUT_TYPE}));
  }catch(error){if(!done){done=true;clearTimeout(timer);reject(error);}}
 };
 try{if(typeof canvas.toBlob==='function')canvas.toBlob(blob=>blob?.size?finish(blob):fallback(),OUTPUT_TYPE,quality);else fallback();}catch{fallback();}
});
export async function normalizeImageFile(file,options={}){
 const originalType=file?.type || '';let stage='read',decoded=null,canvas=null;
 const diagnostics={version:IMAGE_PROCESSING_VERSION,status:'failure',original_type:originalType,original_size:file?.size || 0};
 try{
  if(!(file instanceof File) || !file.size)throw new Error('Invalid file');
  const detectedType=await detectImageType(file);diagnostics.detected_type=detectedType;
  stage='format';if(!['image/jpeg','image/png','image/webp','image/heic','image/heif'].includes(detectedType))throw new Error('Unsupported image');
  // Copy camera data while the input still holds its file, correcting unreliable MIME hints.
  stage='read';const data=await readBuffer(file);let decodable=new Blob([data],{type:detectedType});
  stage='decode';
  try{decoded=await loadImage(decodable);}catch(error){
   if(!['image/heic','image/heif'].includes(detectedType))throw error;
   stage='heic';const {default:heic2any}=await import('heic2any');const converted=await deadline(heic2any({blob:decodable,toType:OUTPUT_TYPE,quality:JPEG_QUALITY}),30000);
   decodable=Array.isArray(converted)?converted[0]:converted;
   if(!(decodable instanceof Blob))throw new Error('HEIC conversion failed');
   stage='decode';decoded=await loadImage(decodable);
  }
  diagnostics.decoder=decoded.method;
  const width=decoded.image.naturalWidth || decoded.image.width,height=decoded.image.naturalHeight || decoded.image.height;
  if(!width || !height)throw new Error('Invalid dimensions');
  diagnostics.source_width=width;diagnostics.source_height=height;
  const limit=Math.min(MAX_BYTES,Number(options.maxSize)>0?Number(options.maxSize):MAX_BYTES);
  let blob,lastError;
  for(const dimension of [MAX_IMAGE_DIMENSION,1600,1280]){
   canvas=document.createElement('canvas');
   const scale=Math.min(1,dimension/Math.max(width,height));
   canvas.width=Math.max(1,Math.round(width*scale));canvas.height=Math.max(1,Math.round(height*scale));
   try{
    stage='draw';const context=canvas.getContext('2d');if(!context)throw new Error('Canvas unavailable');
    context.fillStyle='#ffffff';context.fillRect?.(0,0,canvas.width,canvas.height);
    context.drawImage(decoded.image,0,0,canvas.width,canvas.height);
    stage='export';
    for(const quality of [JPEG_QUALITY,0.76,0.64]){blob=await canvasBlob(canvas,quality);if(blob.size<=limit)break;}
    if(blob?.size<=limit){diagnostics.width=canvas.width;diagnostics.height=canvas.height;break;}
    lastError=new Error('Encoded photo exceeds size limit');stage='size';
   }catch(error){lastError=error;}
   canvas.width=0;canvas.height=0;blob=null;
  }
  if(!blob)throw lastError || new Error('Canvas unavailable');
  const normalized=new File([blob],'photo-'+Date.now()+'.jpg',{type:OUTPUT_TYPE,lastModified:Date.now()});
  diagnostics.status='success';diagnostics.output_size=normalized.size;
  return {file:normalized,previewUrl:URL.createObjectURL(normalized),originalType,normalizedType:OUTPUT_TYPE,converted:true,diagnostics};
 }catch(error){
  diagnostics.stage=stage;diagnostics.code='IMG_'+stage.toUpperCase();
  const message=stage==='format'?IMAGE_PROCESSING_ERROR+' Escolha JPEG, PNG, WebP ou HEIC.':stage==='size'?'A foto não pôde ser reduzida ao tamanho permitido.':IMAGE_PROCESSING_ERROR;
  const processingError=new Error(message+' ['+diagnostics.code+']');processingError.cause=error;processingError.diagnostics=diagnostics;throw processingError;
 }finally{decoded?.dispose();if(canvas){canvas.width=0;canvas.height=0;}}
}
